/**
 * The player for a race replay (useRaceReplay): back a minute, play or pause, on a minute, 1×, 2× or
 * 3× speed, and a scrubber over the race with the race time and the time to go.
 */
import { REPLAY_SPEEDS, REPLAY_STEP_S, clockText } from "./race-replay";
import type { ReplayPlayer } from "./useRaceReplay";

const C = { line: "#1e1e38", border: "#3a3a5c", text: "#e0e0e8", soft: "#cbd5e1", muted: "#9a9ab4", accent: "#F48C34" };

export function ReplayBar({ player, compact = false }: { player: ReplayPlayer; compact?: boolean }) {
  const { timeS, lengthS, scheduledS, playing, speed } = player;
  const btn = `flex items-center justify-center rounded-md border font-semibold ${compact ? "min-h-[44px] min-w-[44px] px-3 text-base" : "h-8 min-w-[2.25rem] px-2.5 text-sm"}`;
  return (
    <div
      role="group"
      aria-label="Replay player"
      className={`flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b ${compact ? "px-3 py-2" : "px-6 py-2"}`}
      style={{ borderColor: C.line, background: "rgba(244,140,52,0.06)" }}
      data-testid="replay-bar"
    >
      <div className="flex items-center gap-1.5">
        <button type="button" className={btn} style={{ borderColor: C.border, color: C.text }} onClick={() => player.step(-REPLAY_STEP_S)} aria-label="Back 1 minute" title="Back 1 minute">
          ⏪
        </button>
        <button
          type="button"
          className={btn}
          style={{ borderColor: C.accent, color: "#0b0b18", background: C.accent }}
          onClick={() => (playing ? player.pause() : player.play())}
          aria-label={playing ? "Pause" : "Play"}
          title={playing ? "Pause" : "Play"}
        >
          {playing ? "❚❚" : "▶"}
        </button>
        <button type="button" className={btn} style={{ borderColor: C.border, color: C.text }} onClick={() => player.step(REPLAY_STEP_S)} aria-label="On 1 minute" title="On 1 minute">
          ⏩
        </button>
      </div>
      <div className="flex items-center gap-1" role="radiogroup" aria-label="Replay speed">
        {REPLAY_SPEEDS.map((s) => (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={speed === s}
            className={btn}
            style={speed === s ? { borderColor: C.accent, color: C.accent } : { borderColor: C.border, color: C.muted }}
            onClick={() => player.setSpeed(s)}
            title={`${s}× speed`}
          >
            {s}×
          </button>
        ))}
      </div>
      <label className="flex min-w-[12rem] flex-1 items-center gap-2 text-xs tabular-nums" style={{ color: C.soft }}>
        <span>{clockText(timeS)}</span>
        <input
          type="range"
          min={0}
          max={lengthS}
          step={10}
          value={Math.round(timeS)}
          onChange={(e) => player.seek(Number(e.target.value))}
          aria-label="Race time"
          className="min-w-0 flex-1"
          style={{ accentColor: C.accent }}
        />
        <span>{clockText(Math.max(0, scheduledS - timeS))} to go</span>
      </label>
    </div>
  );
}
